import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

export default function Home() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearch = (e) => {
    e.preventDefault()
    navigate(`/explore${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`)
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="hero">
        <h1 className="hero-title">
          Find Food Vendors Near You
        </h1>
        <p className="hero-subtitle">
          The most comprehensive database of mobile food vendors in San Francisco.
          Search, explore, and get notified when vendors are nearby.
        </p>
        <form className="hero-search" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Enter a location, vendor name, or cuisine type"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <h3 className="feature-title">Location-Based Search</h3>
            <p className="feature-desc">
              Set your address and search radius. Find vendors within walking
              or driving distance of any location in San Francisco.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <h3 className="feature-title">Instant Notifications</h3>
            <p className="feature-desc">
              Get alerts when new vendors appear, locations change, or
              schedules update. Choose immediate, daily, or weekly updates.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                <line x1="8" y1="2" x2="8" y2="18"/>
                <line x1="16" y1="6" x2="16" y2="22"/>
              </svg>
            </div>
            <h3 className="feature-title">Interactive Map</h3>
            <p className="feature-desc">
              Explore all food vendors on a full interactive map.
              Filter by day, time, vendor type, and cuisine.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '80px 24px', background: 'var(--background)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
            How It Works
          </h2>
          <div style={{ textAlign: 'left' }}>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: 'var(--primary)',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                flexShrink: 0
              }}>1</div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '4px' }}>Create your free account</h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Sign up with just your email address. No password required.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: 'var(--primary)',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                flexShrink: 0
              }}>2</div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '4px' }}>Set your location preferences</h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Enter your address and choose your preferred search radius.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: 'var(--primary)',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                flexShrink: 0
              }}>3</div>
              <div>
                <h4 style={{ fontWeight: '600', marginBottom: '4px' }}>Get personalized updates</h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Receive notifications when vendors, locations, or schedules change near you.
                </p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '32px' }}>
            {user ? (
              <Link to="/dashboard" className="btn btn-primary btn-lg">
                Go to My Feed
              </Link>
            ) : (
              <Link to="/auth" className="btn btn-primary btn-lg">
                Get Started
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>
          Data sourced from{' '}
          <a href="https://data.sfgov.org" target="_blank" rel="noopener noreferrer">
            San Francisco Open Data
          </a>
          {' '} &middot; Updated hourly
        </p>
      </footer>
    </div>
  )
}
